import React, { useState, useRef, useEffect, ReactNode } from "react"
import Text from "../Atoms/Text"

interface TooltipProps {
    children: ReactNode
    closeDelay?: number
    openMargin?: string
    tooltipContent: ReactNode // Changed to ReactNode for a component
    tooltipOriginX?: "left" | "center" | "right"
    tooltipOriginY?: "top" | "center" | "bottom"
    tooltipStyle?: React.CSSProperties
}

const Tooltip: React.FC<TooltipProps> = ({
    children,
    closeDelay = 100,
    openMargin = "2px",
    tooltipContent,
    tooltipOriginX = "center",
    tooltipOriginY = "top",
    tooltipStyle = {}
}) => {
    const [tooltipVisible, setTooltipVisible] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [isTouchDevice, setIsTouchDevice] = useState(
        typeof window !== "undefined" && "ontouchstart" in window
    )
    const tooltipRef = useRef<HTMLDivElement>(null)
    const closeDelayTimeout = useRef<NodeJS.Timeout | null>(null)

    const handleClickOutside = (event: MouseEvent) => {
        if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
            setTooltipVisible(false)
        }
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    // Check if the screen size is mobile
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 768) // Adjust breakpoint as needed
        }

        // Initial check
        checkScreenSize()

        // Add event listener for resize
        window.addEventListener("resize", checkScreenSize)
        return () => {
            window.removeEventListener("resize", checkScreenSize)
        }
    }, [])

    const handleMouseEnter = () => {
        if (!isTouchDevice) {
            if (closeDelayTimeout.current) {
                clearTimeout(closeDelayTimeout.current)
            }
            setTooltipVisible(true)
        }
    }

    const handleMouseLeave = () => {
        if (!isTouchDevice) {
            closeDelayTimeout.current = setTimeout(() => {
                setTooltipVisible(false)
            }, closeDelay)
        }
    }

    const handleTouchStart = () => {
        if (isTouchDevice) {
            setTooltipVisible(prev => !prev)
        }
    }

    const originX = isMobile ? "right" : tooltipOriginX // "right" on mobile, "center" on desktop
    const originY = isMobile ? "bottom" : tooltipOriginY // "top" on mobile, "bottom" on desktop

    const key = `${originX},${originY}`
    const positions: Record<string, React.CSSProperties> = {
        "left,top": { bottom: `calc(100% + ${openMargin})`, left: "0px" },
        "left,center": {
            top: "50%",
            right: `calc(100% + ${openMargin})`,
            transform: "translateY(-50%)"
        },
        "left,bottom": { top: `calc(100% + ${openMargin})`, left: "0px" },
        "center,top": {
            bottom: `calc(100% + ${openMargin})`,
            left: "50%",
            transform: "translateX(-50%)"
        },
        "center,center": {
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)"
        },
        "center,bottom": {
            top: `calc(100% + ${openMargin})`,
            left: "50%",
            transform: "translateX(-50%)"
        },
        "right,top": { bottom: `calc(100% + ${openMargin})`, right: "0px" },
        "right,center": {
            top: "50%",
            left: `calc(100% + ${openMargin})`,
            transform: "translateY(-50%)"
        },
        "right,bottom": {
            top: `calc(100% + ${openMargin})`,
            right: `${isMobile ? "calc(100% + -60px)" : "0px"}`
        }
    }

    return (
        <div
            ref={tooltipRef}
            className="relative inline-block"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
        >
            {children}
            {tooltipVisible && (
                <Text
                    size="extrasmall"
                    weight="regular"
                    className="pointer-events-none absolute z-50 w-[16rem] rounded bg-white p-12 normal-case text-black shadow-lg"
                    style={{ ...positions[key], ...tooltipStyle }}
                >
                    {tooltipContent}
                </Text>
            )}
        </div>
    )
}

export default Tooltip
